import loadQueryParametersFromStore from './middlewares/storeParameters';
import authenticated from './middlewares/authenticated';
import guest from './middlewares/guest';
import log from './middlewares/log';

export const defaultRoutes = [
  {
    name: 'index',
    path: '/',
    component: () => import('../views/index.vue'),
    meta: {
      middleware: authenticated,
    },
  },
  {
    name: 'noAccess',
    path: '/accessDenied',
    component: () => import('../views/accessDenied.vue'),
    meta: {
      middleware: authenticated,
    },
  },
  {
    name: 'reports',
    path: '/reports',
    component: () => import('../views/reports/index.vue'),
    meta: {
      middleware: authenticated,
    },
    children: [
      {
        name: 'printers',
        path: 'printers',
        component: () => import('../views/reports/printers/index.vue'),
        meta: {
          middleware: authenticated,
        },
        children: [
          {
            name: 'printerStatistics',
            path: 'printer-statistics',
            redirect: { name: 'docStat' },
            component: () => import('../views/reports/printers/statistics/index.vue'),
            meta: {
              middleware: [authenticated],
            },
            children: [
              {
                name: 'docStat',
                path: 'doc-stat',
                component: () => import('../views/reports/printers/statistics/documents/doc-stat.vue'),
                meta: {
                  middleware: [authenticated, loadQueryParametersFromStore],
                  requiredStoreParameters: {
                    dateRange: true,
                    printer: true,
                    tableGroup: true,
                    export: true,
                  },
                },
              },
              {
                name: 'printStat',
                path: 'print-stat',
                component: () => import('../views/reports/printers/statistics/print-stat.vue'),
                meta: {
                  middleware: [authenticated, loadQueryParametersFromStore],
                  requiredStoreParameters: {
                    dateRange: true,
                    printer: true,
                  },
                },
              },
              {
                name: 'userStat',
                path: 'user-stat',
                component: () => import('../views/reports/printers/statistics/user-stat.vue'),
                meta: {
                  middleware: [authenticated, loadQueryParametersFromStore],
                  requiredStoreParameters: {
                    dateRange: true,
                  },
                },
              },
            ],
          },
          {
            name: 'employees',
            path: 'employees',
            redirect: { name: 'employeeReport' },
            components: {
              default: () => import('../views/reports/printers/employees/index.vue'),
            },
            meta: {
              middleware: [authenticated],
            },
            children: [
              {
                name: 'employeeReport',
                path: 'employee-report',
                component: () => import('../views/reports/printers/employees/employee-report.vue'),
                meta: {
                  middleware: [authenticated, loadQueryParametersFromStore],
                  requiredStoreParameters: {
                    dateRange: true,
                    tableGroup: true,
                    export: true,
                  },
                },
              },
              {
                name: 'employeePrintReport',
                path: 'employee-print-report',
                component: () => import('../views/reports/printers/statistics/user-stat.vue'),
                meta: {
                  middleware: [authenticated, loadQueryParametersFromStore],
                  requiredStoreParameters: {
                    dateRange: true,
                  },
                },
              },
            ],
          },
          {
            name: 'print',
            path: 'print',
            redirect: { name: 'printerReport' },
            components: {
              default: () => import('../views/reports/printers/printers/index.vue'),
            },
            meta: {
              middleware: [authenticated],
            },
            children: [
              {
                name: 'printerReport',
                path: 'printer-report',
                component: () => import('../views/reports/printers/printers/printer-report.vue'),
                meta: {
                  middleware: [authenticated, loadQueryParametersFromStore],
                  requiredStoreParameters: {
                    dateRange: true,
                    printer: true,
                    tableGroup: true,
                    export: true,
                  },
                },
              },
              {
                name: 'printerPrintReport',
                path: 'printer-print-report',
                component: () => import('../views/reports/printers/statistics/print-stat.vue'),
                meta: {
                  middleware: [authenticated, loadQueryParametersFromStore],
                  requiredStoreParameters: {
                    dateRange: true,
                    printer: true,
                  },
                },
              },
            ],
          },
        ],
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../views/reports/users/index.vue'),
        meta: {
          middleware: authenticated,
        },

        children: [
          {
            name: 'consolidated',
            path: 'consolidated',
            component: () => import('../views/reports/users/consolidated/consolidated-report.vue'),
            meta: {
              middleware: [authenticated, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
                multiSelect: true,
                worktimeOnly: true,
                export: true,
              },
            },
          },
          {
            name: 'consolidatedFull',
            path: 'consolidated-full',
            component: () => import('../views/reports/users/consolidated/consolidated-report.vue'),
            meta: {
              middleware: [authenticated, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
                multiSelect: true,
                worktimeOnly: true,
                export: true,
              },
            },
          },
          {
            name: 'summaryReport',
            path: 'summaryReport',
            component: () => import('../views/reports/users/consolidated/consolidated-report.vue'),
            meta: {
              middleware: [authenticated, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
                multiSelect: true,
                worktimeOnly: true,
              },
            },
          },
          {
            name: 'summaryUserReport',
            path: 'summaryUserReport',
            component: () => import('../views/reports/users/consolidated/consolidated-report.vue'),
            meta: {
              middleware: [authenticated, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
                selectedComputer: true,
                worktimeOnly: true,
              },
            },
          },
          {
            name: 'worktime',
            path: 'worktime',
            redirect: { name: 'heatMap' },
            component: () => import('../views/reports/users/worktime/index.vue'),
            meta: {
              middleware: [authenticated],
            },
            children: [
              {
                name: 'worktimeDetails',
                path: 'worktime-details',
                component: () => import('../views/reports/users/worktime/worktime-report.vue'),
                meta: {
                  middleware: [authenticated, loadQueryParametersFromStore],
                  requiredStoreParameters: {
                    dateRange: true,
                    multiSelect: true,
                    worktimeOnly: true,
                    export: true,
                  },
                },
              },
              {
                name: 'heatMap',
                path: 'heat-map',
                component: () => import('../views/reports/users/worktime/heat-map.vue'),
                meta: {
                  middleware: [authenticated, loadQueryParametersFromStore],
                  requiredStoreParameters: {
                    dateRange: true,
                    multiSelect: true,
                    worktimeOnly: true,
                    switcher: true,
                    dateSelect: true,
                  },
                },
              },
              {
                name: 'disciplineViolations',
                path: 'discipline-violations',
                component: () => import('../views/reports/users/worktime/discipline-violations.vue'),
                meta: {
                  middleware: [authenticated, loadQueryParametersFromStore],
                  requiredStoreParameters: {
                    dateRange: true,
                    multiSelect: true,
                    worktimeOnly: true,
                    dateSelect: true,
                  },
                },
              },
            ],
          },
          {
            name: 'activityFeed',
            path: 'activity-feed',
            component: () => import('../views/reports/users/activity/activity-feed.vue'),
            meta: {
              middleware: [authenticated, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
                multiSelect: true,
                export: true,
              },
            },
          },
          {
            name: 'productivity',
            path: 'productivity',
            component: () => import('../views/reports/users/productivity/productivity-report.vue'),
            meta: {
              middleware: [authenticated, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
                multiSelect: true,
                worktimeOnly: true,
                export: true,
              },
            },
          },
          {
            name: 'appsTime',
            path: 'apps-time',
            component: () => import('../views/reports/users/appsTime/apps-time-report.vue'),
            meta: {
              middleware: [authenticated, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
                multiSelect: true,
                worktimeOnly: true,
                treeSelect: true,
                export: true,
              },
            },
          },
          {
            name: 'programs',
            path: 'programs',
            component: () => import('../views/reports/statistics/programs/program-stat.vue'),
            meta: {
              middleware: [authenticated, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
                multiSelect: true,
              },
            },
          },
          {
            name: 'files',
            path: 'files',
            component: () => import('../views/reports/users/files/files.vue'),
            meta: {
              middleware: [authenticated, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
                multiSelect: true,
                export: false,
              },
            },
          },
        ],
      },
      {
        name: 'internet',
        path: 'internet',
        component: () => import('../views/reports/internet/index.vue'),
        meta: {
          middleware: authenticated,
        },
        children: [
          {
            name: 'websites',
            path: 'websites',
            component: () => import('../views/reports/statistics/websites/website-stat.vue'),
            meta: {
              middleware: [authenticated, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
                multiSelect: true,
              },
            },
          },
          {
            name: 'messengers',
            path: 'messengers',
            component: () => import('../views/reports/internet/messengers.vue'),
            meta: {
              middleware: [authenticated, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
                multiSelect: true,
                export: true,
              },
            },
          },
          {
            name: 'mail',
            path: 'mail',
            component: () => import('../views/reports/internet/mail.vue'),
            meta: {
              middleware: [authenticated, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
                multiSelect: true,
                export: true,
              },
            },
          },
          {
            name: 'detailsMessengers',
            path: 'details-messengers',
            component: () => import('../views/reports/internet/details-messengers.vue'),
            meta: {
              middleware: [authenticated, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
                multiSelect: true,
                export: true,
              },
            },
          },
          {
            name: 'traffic',
            path: 'traffic',
            redirect: { name: 'trafficByGroups' },
            component: () => import('../views/reports/internet/index.vue'),
            meta: {
              middleware: [authenticated],
            },
            children: [
              {
                name: 'trafficByGroups',
                path: 'traffic-by-groups',
                component: () => import('../views/reports/internet/traffic.vue'),
                meta: {
                  middleware: [authenticated, loadQueryParametersFromStore],
                  requiredStoreParameters: {
                    dateRange: true,
                    multiSelect: true,
                    dateSelect: true,
                  },
                },
              },
              {
                name: 'trafficDetailed',
                path: 'traffic-detailed',
                component: () => import('../views/reports/internet/traffic.vue'),
                meta: {
                  middleware: [authenticated, loadQueryParametersFromStore],
                  requiredStoreParameters: {
                    dateRange: true,
                    multiSelect: false,
                    dateSelect: true,
                    selectedComputer: true,
                  },
                },
              },
            ],
          },
        ],
      },
      {
        name: 'microphone',
        path: 'microphone',
        component: () => import('../views/reports/microphone/index.vue'),
        meta: {
          middleware: authenticated,
        },
        children: [
          {
            name: 'microphoneSum',
            path: 'microphone-sum',
            component: () => import('../views/reports/microphone/sum.vue'),
            meta: {
              middleware: [authenticated, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
                multiSelect: true,
                worktimeOnly: true,
                export: true,
              },
            },
          },
          {
            name: 'microphoneDetails',
            path: 'microphone-details',
            component: () => import('../views/reports/microphone/details.vue'),
            meta: {
              middleware: [authenticated, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
                multiSelect: true,
                worktimeOnly: true,
                export: true,
              },
            },
          },
        ],
      },
    ],
  },
  {
    name: 'user-login',
    path: '/user/login',
    component: () => import('../views/user/login.vue'),
    meta: {
      middleware: guest,
    },
  },
  {
    name: 'relations',
    path: '/relations',
    component: () => import('../views/security/relations/relations.vue'),
    meta: {
      middleware: authenticated,
      requiredStoreParameters: {
        multiSelect: true,
      },
    },
  },
  {
    name: 'watermark',
    path: '/watermark',
    component: () => import('../views/security/watermark/watermark.vue'),
    meta: {
      middleware: authenticated,
      requiredStoreParameters: {
        multiSelect: true,
      },
    },
  },
  {
    name: 'notifications',
    path: '/notifications',
    component: () => import('../views/notifications/index.vue'),
    meta: {
      middleware: authenticated,
    },
  },
  {
    name: 'search',
    path: '/search',
    component: () => import('../views/search/index.vue'),
    meta: {
      middleware: authenticated,
    },
  },
  {
    name: 'remoteViewer',
    path: '/remote-viewer',
    component: () => import('../views/vnc/index.vue'),
    meta: {
      middleware: authenticated,
    },
  },
  {
    name: 'fullScreenRemoteViewer',
    path: '/full-screen-remote-viewer/:compId',
    component: () => import('../views/vnc/fullScreen.vue'),
    meta: {
      middleware: authenticated,
    },
  },
  {
    name: 'tasks',
    path: '/tasks',
    component: () => import('../views/tasks/index.vue'),
    meta: {
      middleware: authenticated,
    },
  },
  {
    name: 'computers',
    path: '/computers',
    component: () => import('../views/computers/index.vue'),
    meta: {
      middleware: [authenticated, loadQueryParametersFromStore],
      requiredStoreParameters: {
        dateRange: true,
      },
    },
    children: [
      {
        name: 'computers-computer',
        path: ':computer',
        components: {
          default: () => import('../views/computers/computer/index-default.vue'),
          control: () => import('../views/computers/computer/index-control.vue'),
        },
        meta: {
          middleware: [authenticated, loadQueryParametersFromStore],
          requiredStoreParameters: {
            dateRange: true,
          },
        },
        children: [
          {
            name: 'computers-computer-log-record',
            path: ':log/:record?',
            components: {
              default: () => import('../views/computers/computer/log-record/index-default.vue'),
              control: () => import('../views/computers/computer/log-record/index-control.vue'),
            },

            meta: {
              middleware: [authenticated, log, loadQueryParametersFromStore],
              requiredStoreParameters: {
                dateRange: true,
              },
            },
          },
        ],
      },
    ],
  },
  {
    name: 'advanced',
    path: '/advanced',
    component: () => import('../views/computers/advanced/index.vue'),
    meta: {
      middleware: [authenticated, loadQueryParametersFromStore],
      requiredStoreParameters: {
        dateRange: true,
      },
    },
  },
  {
    name: 'dashboard',
    path: '/dashboard',
    component: () => import('../views/computers/dashboard/index.vue'),
    meta: {
      middleware: [authenticated, loadQueryParametersFromStore],
      requiredStoreParameters: {
        dateRange: true,
      },
    },
  },
  {
    name: 'settings',
    path: '/settings',
    component: () => import('../views/settings/index.vue'),
    meta: {
      middleware: authenticated,
    },
    children: [
      {
        name: 'general',
        path: 'general',
        component: () => import('../views/settings/general/generalSettings.vue'),
        meta: {
          middleware: authenticated,
          requiredStoreParameters: {},
        },
      },
      {
        name: 'agents',
        path: 'agents',
        component: () => import('../views/settings/agents/agents.vue'),
        meta: {
          middleware: [authenticated],
          requiredStoreParameters: {
            generalSettings: true,
          },
        },
        children: [
          {
            name: 'agents-agent',
            path: ':type/:computer/:compName/:userName',
            component: () => import('../views/settings/agents/agent/index.vue'),
            meta: {
              middleware: authenticated,
              requiredStoreParameters: {
                generalSettings: true,
              },
            },
            children: [
              {
                name: 'agents-agent-settings',
                path: 'settings/:log',
                component: () => import('../views/settings/agents/agent/settings/index.vue'),
                meta: {
                  middleware: [authenticated],
                  requiredStoreParameters: {
                    generalSettings: true,
                  },
                },
              },
              {
                name: 'agents-agent-security',
                path: 'security/:log',
                component: () => import('../views/settings/agents/agent/security/index.vue'),
                meta: {
                  middleware: [authenticated],
                  requiredStoreParameters: {
                    generalSettings: true,
                  },
                },
              },
              {
                name: 'agents-agent-notifications',
                path: 'notifications/:log',
                component: () => import('../views/settings/agents/agent/notifications/index.vue'),
                meta: {
                  middleware: [authenticated],
                  requiredStoreParameters: {
                    generalSettings: true,
                  },
                },
              },
              {
                name: 'agents-agent-irregular',
                path: 'irregular/:log',
                component: () => import('../views/settings/agents/agent/notifications/irregular.vue'),
                meta: {
                  middleware: [authenticated],
                  requiredStoreParameters: {
                    generalSettings: true,
                  },
                },
              },
              {
                name: 'agents-agent-search',
                path: 'advanced-search/:log',
                component: () => import('../views/settings/agents/agent/search/index.vue'),
                meta: {
                  middleware: [authenticated],
                  requiredStoreParameters: {
                    generalSettings: true,
                  },
                },
              },
              {
                name: 'agents-agent-criticalevents',
                path: 'critical-events/:log',
                component: () => import('../views/settings/agents/agent/criticalevents/index.vue'),
                meta: {
                  middleware: [authenticated],
                  requiredStoreParameters: {
                    generalSettings: true,
                  },
                },
              },
            ],
          },
        ],
      },
      {
        name: 'controls',
        path: 'controls',
        component: () => import('../views/settings/control/control.vue'),
        meta: {
          middleware: authenticated,
          requiredStoreParameters: {},
        },
        children: [
          {
            name: 'controls-control',
            path: ':user',
            component: () => import('../views/settings/control/controlParameters.vue'),
            meta: {
              middleware: authenticated,
              requiredStoreParameters: {},
            },
          },
        ],
      },
      {
        name: 'category',
        path: 'category',
        component: () => import('../views/settings/categories/categories.vue'),
        meta: {
          middleware: authenticated,
          requiredStoreParameters: {},
        },
      },
      {
        name: 'printPrice',
        path: 'print-price',
        component: () => import('../views/settings/printPrice/printPrice.vue'),
        meta: {
          middleware: authenticated,
          requiredStoreParameters: {},
        },
      },

      {
        name: 'schedule',
        path: 'schedule',
        component: () => import('../views/settings/schedule/schedule.vue'),
        meta: {
          middleware: authenticated,
          requiredStoreParameters: {},
        },
      },
      {
        name: 'diagnostics',
        path: 'diagnostics',
        component: () => import('../views/settings/diagnostics/diagnostics.vue'),
        meta: {
          middleware: authenticated,
          requiredStoreParameters: {},
        },
      },
    ],
  },
  {
    name: 'sysLogs',
    path: '/sysLogs',
    component: () => import('../views/notifications/index.vue'),
    meta: {
      middleware: authenticated,
      requiredStoreParameters: {},
    },
  },
  {
    name: 'program',
    path: '/program',
    component: () => import('../views/program/index.vue'),
    meta: {
      middleware: authenticated,
    },
    children: [
      {
        name: 'updatesProgram',
        path: 'updates-program',
        component: () => import('../views/program/updates/updatesProgram.vue'),
        meta: {
          middleware: authenticated,
          requiredStoreParameters: {},
        },
      },
      {
        name: 'aboutProgram',
        path: 'about-program',
        component: () => import('../views/program/about/aboutProgram.vue'),
        meta: {
          middleware: authenticated,
          requiredStoreParameters: {},
        },
      },
    ],
  },
];
